<script setup>
import ArticleHeader from '@/components/article/ArticleHeader.vue'
import SocialArticle from '@/components/buttons/SocialArticle.vue'
import MbBox from '@/components/boxes/MbBox.vue'
import ModuleBannerAd from '@/components/modules/ModuleBannerAd.vue'
import ModuleBoxes from '@/components/modules/ModuleBoxes.vue'
import ArtDetailBoxes from '@/components/boxes/ArtDetailBoxes.vue'
import SeeMoreBtn from '@/components/buttons/SeeMoreBtn.vue'
import { computed, onMounted, watch } from 'vue'
import { useSectionsStore } from '@/stores/section'
import { storeToRefs } from 'pinia'
import { useBreakpoint } from '@/composables/useBreakpoints'
import { useNavigationStore } from '@/stores/navigation'
import PhotoLoader from '@/components/elements/PhotoLoader.vue'
import ModuleRightSidebar from '@/components/modules/ModuleRightSidebar.vue'
import ModulePlayer from '@/components/modules/ModulePlayer.vue'
import ArticleGallery from '@/components/article/ArticleGallery.vue'
import ArticleVideo from '@/components/article/ArticleVideo.vue'
import ArticleRecommend from '@/components/article/ArticleRecommend.vue'
import ArticleRecommendRightColumn from '@/components/article/ArticleRecommendRightColumn.vue'
import ViewRequest from '@/components/layout/ViewRequest.vue'

const sectionsStore = useSectionsStore()
const { itemsNewsDetails, pageSettings, seeMoreItems } = storeToRefs(sectionsStore)
const { fetchNewsDetails, fetchSeeMore, fetchBlockListByPage } = sectionsStore

const navigationStore = useNavigationStore()
const { startLoaderPage, endLoaderPage } = navigationStore

const newsId = computed(() => pageSettings.value.id)

const itemNews = computed(() => itemsNewsDetails.value[newsId.value])

const firstTwoItems = computed(() => seeMoreItems.value.slice(0, 2))
const otherItems = computed(() => seeMoreItems.value.slice(2, 5))

const showMore = computed(() => itemNews.value?.breadcrumbs?.[0])

const idSidebar = computed(() => itemNews.value?.sidebar_id ?? null)
const isIdSidebar = computed(() => (idSidebar.value ? idSidebar.value : null))
const recommendIds = computed(() => itemNews.value?.breadcrumbs?.[0])

const { isFullHd, isWidescreen, isDesktop, isTablet, isMobile } = useBreakpoint()
const sizeRenderTwoBox = computed(() =>
  isFullHd.value
    ? '379px'
    : '' || isWidescreen.value
    ? '308px'
    : '' || isDesktop.value
    ? '272px'
    : '' || isTablet.value
    ? '268px'
    : '' || isMobile.value
    ? 'auto'
    : ''
)

const sizeRenderThreeBox = computed(() =>
  isFullHd.value
    ? '248px'
    : '' || isWidescreen.value
    ? '201px'
    : '' || isDesktop.value
    ? '177px'
    : '' || isTablet.value
    ? '175px'
    : '' || isMobile.value
    ? 'auto'
    : ''
)

onMounted(() => {
  startLoaderPage()
  fetchNewsDetails(newsId.value)
    .then(() => {
      fetchBlockListByPage(idSidebar.value)
    })
    .finally(() => {
      endLoaderPage()
    })
  fetchSeeMore(newsId.value)
})
</script>

<template>
  <view-request :obj-id="newsId" />
  <section class="article">
    <div v-if="itemNews" class="container">
      <article-header
        :title="itemNews.title"
        :release-date="itemNews.release_date"
        :modify-date="itemNews.modify_date"
        :publication-start-date="itemNews.publication_start"
        :share-url="itemNews.url"
        :signature="itemNews.signature"
        :authorInfo="itemNews.author"
      />
      <div></div>
      <div class="article__layout columns">
        <div
          class="column is-8-desktop is-12-tablet is-12-mobile"
          :class="{ 'is-12-desktop': !isIdSidebar }"
        >
          <div v-if="itemNews.video">
            <div class="article__video-player">
              <module-player :video-id="itemNews.video._id" />
            </div>
          </div>

          <figure class="article__photo" v-if="itemNews.image && !itemNews.video">
            <photo-loader
              :image="itemNews.image"
              height-skeletor="540"
              :altImg="itemNews?.image?.alt"
              :imgWidth="itemNews.image?.width"
              :imgHeight="itemNews.image?.height"
            />
            <figcaption>
              <div class="article__rectangle"></div>
              <div class="article__image-title">{{ itemNews.image?.title }}</div>
            </figcaption>
          </figure>

          <div class="article__paragraph">
            <p v-if="itemNews.head" class="article__heading" v-html="itemNews.head" />

            <div
              v-for="(paragraph, key) in itemNews.paragraphs"
              :key="key"
              class="article__paragraph-item"
            >
              <mb-box
                v-if="paragraph.link"
                :hideBottomSection="true"
                class="article__right-box"
                :boxItems="paragraph.link"
              />
              <span v-html="paragraph.text" />
              <article-video v-if="paragraph.video" :context="paragraph.video" />
              <figure class="article__photo" v-if="paragraph.image">
                <photo-loader
                  :image="paragraph.image"
                  height-skeletor="540"
                  :altImg="paragraph?.image?.alt"
                  :imgWidth="paragraph.image?.width"
                  :imgHeight="paragraph.image?.height"
                />
                <figcaption>
                  <div class="article__rectangle"></div>
                  <div class="article__image-title">{{ paragraph.image?.title }}</div>
                </figcaption>
              </figure>
              <article-gallery v-if="paragraph.image_gallery" :context="paragraph.image_gallery" />
            </div>

            <module-banner-ad />
            <div class="article__source">
              <span v-if="itemNews.point_of_origin"
                >źródło:
                <span class="article__source-text">{{ itemNews.point_of_origin }}</span>
              </span>
              <span class="am-article__tag"></span>
            </div>

            <div class="article-tags">
              <a
                v-for="(tag, key) in itemNews.tags"
                :href="tag.url"
                :key="key"
                class="article-tags__tag"
              >
                {{ `#${tag.title}` }}
              </a>
            </div>

            <social-article
              :isCommentBox="true"
              :title="itemNews.title"
              :share-url="itemNews.url"
            />

            <ArticleRecommend :context="recommendIds" />
          </div>
        </div>

        <div class="column is-4-desktop">
          <div :class="['article-right', { 'article-right__sticky': recommendIds }]">
            <template v-if="isIdSidebar">
              <ModuleRightSidebar :category-sidebar-id="idSidebar" :isSticky="!recommendIds" />
            </template>
            <!-- <template v-if="recommendIds">
              <ArticleRecommendRightColumn :context="recommendIds" />
            </template> -->
          </div>
        </div>
      </div>
    </div>
  </section>

  <section class="article__see-more" v-if="seeMoreItems.length">
    <art-detail-boxes />
    <ModuleBoxes
      :mbBoxItems="2"
      :isFullMobile="true"
      :isCountColumn="true"
      :isSmallPlay="true"
      :boxItems="firstTwoItems"
      :maxLineTitleTwoBoxes="true"
      :boxSkieletors="{ height: sizeRenderTwoBox }"
      class="article__two-boxes"
    />
    <ModuleBoxes
      :mbBoxItems="3"
      :isFullMobile="true"
      :isCountColumn="true"
      :isSmallPlay="true"
      :boxItems="otherItems"
      :maxLineTitleThreeBoxes="true"
      :boxSkieletors="{ height: sizeRenderThreeBox }"
      class="article__three-boxes"
    />
    <see-more-btn v-if="showMore" :show-more="showMore" />
  </section>
</template>

<style lang="scss">
@import '@/assets/scss/views/article-page';
@import '@/assets/scss/utils/media';
</style>
